/**
 * Wie oft etwas neu gezeichnet wird.
 *
 * Beim Abspielen wandert der Abspielkopf sechzigmal in der Sekunde. Alles,
 * was ihn aus dem Speicher liest, zeichnet sich damit sechzigmal in der
 * Sekunde neu -- und auf dem Telefon ist das genau die Zeit, die dem Dekoder
 * fehlt (7.27). Die Vorschau *muss* das: sie zeigt das Bild an dieser Stelle.
 * Die Zeitleiste muss es nicht: unter der feststehenden Linie schiebt sich
 * der Schnitt vorbei, und Schieben ist kein Neuzeichnen.
 *
 * Nur ist „muss nicht“ eine Behauptung, solange niemand nachzählt. Also zählt
 * hier jemand nach: ein Hochzählen je Durchlauf, und ein Weg, die Zahlen von
 * außen zu lesen. Der Durchgang setzt sie zurück, lässt anderthalb Sekunden
 * abspielen und liest sie wieder -- damit steht die Behauptung als Zahl da
 * und nicht als Absicht.
 *
 * Dasselbe Vorgehen wie bei `window.__ton`: ein Instrument, das immer
 * mitläuft, weil ein Instrument, das man erst einbauen muss, im Ernstfall
 * nicht da ist.
 */

const zaehler = new Map<string, number>();

/** Einmal je Durchlauf aufrufen, ganz oben im Bauteil. */
export function zaehleBild(was: string): void {
  zaehler.set(was, (zaehler.get(was) ?? 0) + 1);
}

export function bilder(): Record<string, number> {
  return Object.fromEntries(zaehler);
}

export function bilderZuruecksetzen(): void {
  zaehler.clear();
}

/**
 * Und wie lange etwas dauert.
 *
 * „Die Vorschau rechnet zu viel“ ist eine Behauptung, solange niemand sie in
 * Millisekunden hinschreibt (7.27a sagt selbst: erst messen, dann bauen).
 * `miss` legt die Zeit unter einem Namen ab; gezählt wird gleich mit, sonst
 * sagt eine Summe nichts.
 *
 * Der Rechner hier ist kein Telefon -- die absoluten Zahlen taugen nicht für
 * das iPhone. Das Verhältnis taugt: was hier ein Zehntel einer Millisekunde
 * je Bild braucht, ist auch zehnmal langsamer noch keine Ursache.
 */
const zeiten = new Map<string, { ms: number; n: number }>();

export function miss<T>(was: string, tue: () => T): T {
  const start = performance.now();
  try {
    return tue();
  } finally {
    const stand = zeiten.get(was) ?? { ms: 0, n: 0 };
    stand.ms += performance.now() - start;
    stand.n += 1;
    zeiten.set(was, stand);
  }
}

export function zeitenBericht(): Record<string, { ms: number; n: number; je: number }> {
  const raus: Record<string, { ms: number; n: number; je: number }> = {};
  for (const [was, { ms, n }] of zeiten) {
    raus[was] = { ms: Math.round(ms * 100) / 100, n, je: Math.round((ms / Math.max(1, n)) * 1000) / 1000 };
  }
  return raus;
}

declare global {
  interface Window {
    __bilder?: () => string;
    __bilderZurueck?: () => void;
    __zeiten?: () => string;
    /** Was an einer Stelle der Leinwand steht (7.69a) -- siehe Preview.tsx. */
    __leinwand?: (x?: number, y?: number) => string;
    /** Das ganze Bild der Leinwand als PNG-Adresse (7.136) -- fuer den Vergleich mit dem Film. */
    __leinwandBild?: () => string;
    /** Wie oft gezeichnet und hochgeladen wird, und wie lange das dauert (7.69h). */
    __leinwandBericht?: () => string;
    __leinwandZurueck?: () => void;
    /** Wer gerade den Takt angibt: eine Clip-Kennung, `wartet:<Clip>` oder `wanduhr` (7.69l). */
    __taktgeber?: () => string;
  }
}

if (typeof window !== 'undefined') {
  window.__bilder = () => JSON.stringify(bilder());
  window.__bilderZurueck = () => {
    bilderZuruecksetzen();
    zeiten.clear();
  };
  window.__zeiten = () => JSON.stringify(zeitenBericht());
}
